import React, { useContext, useEffect, useMemo, useState } from 'react';
import { translate } from '@utils/i18n';
import { AssigneeType } from '@enums/assignee-type.enum';
import { VisitPriority } from '@enums/visit-priority.enum';
import { VisitableType } from '@enums/visitable-type-enum';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { Duration } from 'luxon';
import apiClient from '@utils/api-client';
import { dateClient } from '@utils/date-client';
import VisitPriorityDropdown from '@components/Dropdowns/VisitPriorityDropdown';
import InputTextArea, { StyleVariant } from '@components/InputTextArea/InputTextArea';
import { transformDayJstoDate } from '@utils/dateUtils';
import usePatchBuilding from '@hooks/requests/usePatchBuilding';
import TooltipText from '@components/v2/TooltipText';
import { checkAvailableTechniciansPath } from '../../../../routes';
import VisitStatusDropdown from '../../../../components/Dropdowns/VisitStatusDropdown';
import { VisitModalContext } from '../VisitModalContext';
import Error from '../../../../components/Error';
import TechSubToggle from './tech-sub-toggle';
import SubcontractorDropdown from '../../../../components/Dropdowns/SubcontractorDropdown';
import TechnicianDropdown from '../../../../components/Dropdowns/TechnicianDropdown';
import TimeZoneDropdown from './time-zone-dropdown';
import { Loader, LoaderSizes } from '../../../../components/Loader/Loader';
import { TimeZoneContext } from '../../../../contexts/timezone-context';
import { Button } from '@ui';
function Form(params) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const namespace = 'features.modals.visit_modal.form';
    const { tenant, visit, visitStatusList, visitPriorityList, subcontractorList, technicianList, timeZoneList, saving, closeModal, saveVisit } = useContext(VisitModalContext);
    const { timeZonesMapping } = useContext(TimeZoneContext);
    if (!visit || !tenant)
        return React.createElement(Error, { error: "Unable to load form." });
    const visitState = Object.assign(Object.assign({}, visit), { start: visit.start.tz(timeZonesMapping[visit.timeZone]), end: visit.end.tz(timeZonesMapping[visit.timeZone]) });
    const [formData, setFormData] = useState(visitState);
    const [isNoteVisible, setIsNoteVisible] = useState(!!visit.visitNotes);
    useEffect(() => {
        setIsNoteVisible(!!formData.visitNotes);
    }, [formData.visitNotes]);
    const [availabilityNotification, setAvailabilityNotification] = useState();
    const isHighPriority = formData.priority === VisitPriority.HIGH;
    const { save: saveBuilding, saving: savingBuilding, result: savedBuilding } = usePatchBuilding();
    const showSetTimeZoneButton = ((_a = formData.building) === null || _a === void 0 ? void 0 : _a.timeZoneOverride) === '' && formData.timeZone !== ((_b = formData.building) === null || _b === void 0 ? void 0 : _b.timeZone);
    const showResetTimeZoneButton = ((_c = formData.building) === null || _c === void 0 ? void 0 : _c.timeZoneOverride) !== '' && formData.timeZone !== ((_d = formData.building) === null || _d === void 0 ? void 0 : _d.timeZone);
    const defaultSubcontractor = useMemo(() => subcontractorList.find((sub) => { var _a; return sub.id === ((_a = visit.building) === null || _a === void 0 ? void 0 : _a.defaultSubcontractorId); }), [subcontractorList, (_e = visit.building) === null || _e === void 0 ? void 0 : _e.defaultSubcontractorId]);
    const defaultTech = useMemo(() => {
        var _a, _b;
        const idToCompare = visit.visitable.type === VisitableType.SERVICE
            ? (_a = visit.building) === null || _a === void 0 ? void 0 : _a.defaultServiceTechId
            : (_b = visit.building) === null || _b === void 0 ? void 0 : _b.defaultTechId;
        return technicianList.find((tech) => tech.id === idToCompare);
    }, [technicianList, (_f = visit.building) === null || _f === void 0 ? void 0 : _f.defaultServiceTechId, (_g = visit.building) === null || _g === void 0 ? void 0 : _g.defaultTechId]);
    const handleStatusChange = (newStatus) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { status: newStatus })));
    };
    const handlePriorityChange = (newPriority) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { priority: newPriority })));
    };
    const handlePriorityNoteChange = (newPriorityNote) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { priorityNote: newPriorityNote })));
    };
    const handleVisitNotesChange = (newVisitNotes) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { visitNotes: newVisitNotes })));
    };
    const handleTimeZoneChange = (newTimeZone) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { timeZone: newTimeZone })));
    };
    const handleTimeZoneSet = () => {
        const { building } = formData;
        if (!building)
            return;
        saveBuilding(Object.assign(Object.assign({}, building), { timeZoneOverride: formData.timeZone }));
    };
    const handleTimeZoneReset = () => {
        const { building } = formData;
        if (!building)
            return;
        if (formData.building) {
            setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { timeZone: building.timeZone })));
        }
    };
    useEffect(() => {
        if (savedBuilding) {
            setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { building: savedBuilding })));
        }
    }, [savedBuilding]);
    const handleAssigneeTypeChange = (type) => {
        var _a, _b, _c, _d, _e, _f;
        const newAssignee = type === AssigneeType.SUBCONTRACTOR
            ? {
                type: AssigneeType.SUBCONTRACTOR,
                key: `${AssigneeType.SUBCONTRACTOR}-${visit.assignee.type === AssigneeType.SUBCONTRACTOR && ((_a = visit.assignee.info) === null || _a === void 0 ? void 0 : _a.id)
                    ? visit.assignee.info.id
                    : (_b = defaultSubcontractor === null || defaultSubcontractor === void 0 ? void 0 : defaultSubcontractor.id) !== null && _b !== void 0 ? _b : 'unassigned'}`,
                info: visit.assignee.type === AssigneeType.SUBCONTRACTOR && ((_c = visit.assignee.info) === null || _c === void 0 ? void 0 : _c.id)
                    ? visit.assignee.info
                    : defaultSubcontractor !== null && defaultSubcontractor !== void 0 ? defaultSubcontractor : null
            }
            : {
                type: AssigneeType.TECHNICIAN,
                key: `${AssigneeType.TECHNICIAN}-${visit.assignee.type === AssigneeType.TECHNICIAN && ((_d = visit.assignee.info) === null || _d === void 0 ? void 0 : _d.id)
                    ? visit.assignee.info.id
                    : (_e = defaultTech === null || defaultTech === void 0 ? void 0 : defaultTech.id) !== null && _e !== void 0 ? _e : 'unassigned'}`,
                info: visit.assignee.type === AssigneeType.TECHNICIAN && ((_f = visit.assignee.info) === null || _f === void 0 ? void 0 : _f.id)
                    ? visit.assignee.info
                    : defaultTech !== null && defaultTech !== void 0 ? defaultTech : null
            };
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { assignee: newAssignee })));
    };
    const handleSubcontractorChange = (subcontractor) => {
        setFormData((prevState) => {
            var _a;
            return (Object.assign(Object.assign({}, prevState), { assignee: {
                    type: AssigneeType.SUBCONTRACTOR,
                    key: `${AssigneeType.SUBCONTRACTOR}-${(_a = subcontractor === null || subcontractor === void 0 ? void 0 : subcontractor.id) !== null && _a !== void 0 ? _a : 'unassigned'}`,
                    info: subcontractor
                } }));
        });
    };
    const handleTechnicianChange = (technician) => {
        setFormData((prevState) => {
            var _a;
            return (Object.assign(Object.assign({}, prevState), { assignee: {
                    type: AssigneeType.TECHNICIAN,
                    key: `${AssigneeType.TECHNICIAN}-${(_a = technician === null || technician === void 0 ? void 0 : technician.id) !== null && _a !== void 0 ? _a : 'unassigned'}`,
                    info: technician
                } }));
        });
    };
    const handlePONumberChange = (value) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { poNumber: value })));
    };
    const handleBillingLimitChange = (value) => {
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { billingLimit: value })));
    };
    const handleDateChange = (e) => {
        const newDate = e.value;
        if (!newDate)
            return;
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { start: dateClient(newDate)
                .tz(timeZonesMapping[prevState.timeZone])
                .year(newDate.getFullYear())
                .month(newDate.getMonth())
                .date(newDate.getDate())
                .hour(prevState.start.hour())
                .minute(prevState.start.minute())
                .second(0)
                .millisecond(0) })));
    };
    const handleTimeChange = (e) => {
        const newTime = e.value;
        if (!newTime)
            return;
        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { start: dateClient(prevState.start)
                .tz(timeZonesMapping[prevState.timeZone])
                .year(prevState.start.year())
                .month(prevState.start.month())
                .date(prevState.start.date())
                .hour(newTime.getHours())
                .minute(newTime.getMinutes())
                .second(0)
                .millisecond(0) })));
    };
    const kendoDate = useMemo(() => {
        return transformDayJstoDate(formData.start);
    }, [formData.start]);
    const kendoTime = useMemo(() => {
        return transformDayJstoDate(formData.start);
    }, [formData.start]);
    const handleDurationChange = (newDuration) => {
        setFormData((old) => (Object.assign(Object.assign({}, old), { durationMins: Duration.fromObject(Object.assign({ days: duration.days, hours: duration.hours, minutes: duration.minutes }, newDuration)).as('minutes') })));
    };
    const handleSave = () => {
        const start = dateClient(formData.start).tz(timeZonesMapping[formData.timeZone], true);
        saveVisit(Object.assign(Object.assign({}, formData), { start, end: start.add(formData.durationMins, 'minutes') }));
    };
    const duration = useMemo(() => Duration.fromObject({ minutes: formData.durationMins }).shiftTo('days', 'hours', 'minutes'), [formData.durationMins]);
    const assigneeDropdown = useMemo(() => {
        var _a, _b;
        if (formData.assignee.type === AssigneeType.SUBCONTRACTOR)
            return (React.createElement(SubcontractorDropdown, { subcontractorList: subcontractorList, selectedId: (_a = formData.assignee.info) === null || _a === void 0 ? void 0 : _a.id, disabled: params.readonly, onChange: handleSubcontractorChange }));
        return (React.createElement(TechnicianDropdown, { technicianList: technicianList, selectedId: (_b = formData.assignee.info) === null || _b === void 0 ? void 0 : _b.id, disabled: params.readonly, onChange: handleTechnicianChange }));
    }, [formData.assignee]);
    const subcontractorOnlyFields = useMemo(() => {
        if (formData.assignee.type === AssigneeType.SUBCONTRACTOR && formData.visitable.type === VisitableType.SERVICE) {
            return (React.createElement("fieldset", { className: "form__set--horizontal--compact" },
                React.createElement("div", { className: "qmb-input--text--x-half form__field" },
                    React.createElement("input", { id: "po_number", value: formData.poNumber, onChange: (e) => handlePONumberChange(e.target.value), disabled: params.readonly }),
                    React.createElement("label", { className: "qmb-label", htmlFor: "po_number" }, "PO #")),
                React.createElement("div", { className: "qmb-input--text--x-half form__field" },
                    React.createElement("input", { id: "billing_limit", type: "number", min: "0", value: formData.billingLimit, onChange: (e) => handleBillingLimitChange(Number(e.target.value)), disabled: params.readonly }),
                    React.createElement("label", { className: "qmb-label", htmlFor: "billing_limit" }, "Billing Limit"))));
        }
        return null;
    }, [formData.assignee]);
    const assignedTypeToggleValue = useMemo(() => {
        return formData.assignee.type;
    }, [formData.assignee]);
    const AvailabilityContent = useMemo(() => {
        if (availabilityNotification) {
            return (React.createElement("div", { style: { height: '40px', margin: 'auto' } },
                availabilityNotification.message,
                React.createElement("br", null),
                availabilityNotification.data && (React.createElement("a", { href: availabilityNotification.data.link, target: "_blank", rel: "noreferrer" }, availabilityNotification.data.textLink))));
        }
        return null;
    }, [availabilityNotification]);
    useEffect(() => {
        var _a;
        setAvailabilityNotification(undefined);
        if (formData.assignee.type === AssigneeType.TECHNICIAN) {
            apiClient
                .get(checkAvailableTechniciansPath({ format: 'json' }), {
                params: {
                    defaultObjectName: 'Visit',
                    defaultObjectId: visit.id,
                    technicianId: (_a = formData.assignee.info) === null || _a === void 0 ? void 0 : _a.id,
                    scheduledDate: dateClient(formData.start).tz(timeZonesMapping[formData.timeZone]).toISOString(),
                    timeZone: formData.timeZone,
                    duration: formData.durationMins
                }
            })
                .catch((error) => {
                setAvailabilityNotification({
                    message: error.response.data.message,
                    data: error.response.data.data
                });
            });
        }
    }, [formData.start, formData.assignee]);
    const preventDateBackspace = (e) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
        }
    };
    return (React.createElement("div", { className: "dialog__section" },
        React.createElement("div", { className: "dialog__header--section" },
            React.createElement("h2", { className: "dialog__title" }, translate('scheduling_details', { namespace }))),
        React.createElement(TechSubToggle, { render: tenant.flags.subcontractorsEnabled, type: assignedTypeToggleValue, onChange: handleAssigneeTypeChange }),
        React.createElement("fieldset", { className: "form__set--vertical--compact" },
            assigneeDropdown,
            visit.canUpdateStatus && !visit.hasPendingIssues && (React.createElement(VisitStatusDropdown, { statusList: visitStatusList, selectedElement: formData.status, disabled: params.readonly, onChange: handleStatusChange })),
            (!visit.canUpdateStatus || visit.hasPendingIssues) && (React.createElement(TooltipText, { multiline: true, text: visit.hasPendingIssues
                    ? translate('there_are_outstanding_inspection_assistant_issues', {
                        namespace: 'features.inspections_index.table'
                    })
                    : translate('status_change_not_allowed', { namespace }) },
                React.createElement(VisitStatusDropdown, { statusList: visitStatusList, selectedElement: formData.status, disabled: true, onChange: handleStatusChange })))),
        subcontractorOnlyFields,
        ((_h = tenant === null || tenant === void 0 ? void 0 : tenant.flags) === null || _h === void 0 ? void 0 : _h.hasMultiTimezone) && (React.createElement("fieldset", { className: "form__set--vertical--compact" },
            React.createElement(TimeZoneDropdown, { timeZoneList: timeZoneList, selectedValue: formData.timeZone, disabled: params.readonly, onChange: handleTimeZoneChange, onSet: handleTimeZoneSet, onReset: handleTimeZoneReset, showSetTimeZoneButton: showSetTimeZoneButton, showResetTimeZoneButton: showResetTimeZoneButton, buildingTimeZone: (_j = formData.building) === null || _j === void 0 ? void 0 : _j.timeZone, saving: savingBuilding }))),
        React.createElement("fieldset", { className: "form__set--horizontal--compact" },
            React.createElement("div", { className: "qmb-input--text--x-half form__field", onKeyDown: preventDateBackspace },
                React.createElement(DatePicker, { value: kendoDate, onChange: handleDateChange, disabled: params.readonly }),
                React.createElement("label", { className: "qmb-label" }, translate('scheduled_date', { namespace }))),
            React.createElement("div", { className: "qmb-input--text--x-half form__field", onKeyDown: preventDateBackspace },
                React.createElement(TimePicker, { value: kendoTime, onChange: handleTimeChange, disabled: params.readonly }),
                React.createElement("label", { className: "qmb-label" }, translate('scheduled_time', { namespace })))),
        React.createElement("fieldset", { className: "form__set--horizontal--compact" },
            React.createElement("div", { className: "form__field--x-quarter" },
                React.createElement("span", { className: "qmb-label", style: { width: '100%' } }, translate('duration', { namespace }))),
            React.createElement("div", { className: "qmb-input--number--x-quarter duration-field" },
                React.createElement("input", { type: "number", id: "duration_days", min: "0", value: duration.days, onChange: (e) => handleDurationChange({ days: e.target.value || 0 }), disabled: params.readonly }),
                React.createElement("label", { className: "qmb-label", htmlFor: "duration_days" }, translate('days', { namespace }))),
            React.createElement("div", { className: "qmb-input--number--x-quarter duration-field" },
                React.createElement("input", { type: "number", id: "duration_hours", min: "0", max: "23", value: duration.hours, onChange: (e) => handleDurationChange({ hours: e.target.value || 0 }), disabled: params.readonly }),
                React.createElement("label", { className: "qmb-label", htmlFor: "duration_hours" }, translate('hours', { namespace }))),
            React.createElement("div", { className: "qmb-input--number--x-quarter duration-field" },
                React.createElement("input", { type: "number", id: "duration_mins", min: "0", max: "59", value: duration.minutes, onChange: (e) => handleDurationChange({ minutes: e.target.value || 0 }), disabled: params.readonly }),
                React.createElement("label", { className: "qmb-label", htmlFor: "duration_mins" }, translate('minutes', { namespace })))),
        React.createElement("fieldset", { className: "form__set--vertical--compact" },
            !isNoteVisible && (React.createElement("a", { href: "#", onClick: () => setIsNoteVisible(true) }, translate('add_visit_note', { namespace: 'features.work_orders.visits_section' }))),
            isNoteVisible && (React.createElement("div", { className: "formSection vertical visit-notes-input-wrapper" },
                React.createElement(InputTextArea, { id: "visit_notes", name: "visit_notes", className: "visit-note-field", label: translate('visit_notes', { namespace: 'features.work_orders.visits_section' }), value: (_k = formData.visitNotes) !== null && _k !== void 0 ? _k : '', onChange: handleVisitNotesChange, buttonControlled: false, variant: StyleVariant.Light }),
                React.createElement("button", { type: "button", className: "close-note-button close-note-button--kendo", onClick: () => {
                        setIsNoteVisible(false);
                        setFormData((prevState) => (Object.assign(Object.assign({}, prevState), { visitNotes: '' })));
                    } }, "\u00D7")))),
        React.createElement("fieldset", { className: "form__set--vertical--compact" },
            React.createElement(VisitPriorityDropdown, { visitPriorityList: visitPriorityList, selectedValue: formData.priority, onChange: handlePriorityChange, disabled: params.readonly }),
            isHighPriority && (React.createElement(InputTextArea, { id: "priority_note", name: "priority_note", value: (_l = formData.priorityNote) !== null && _l !== void 0 ? _l : '', onChange: handlePriorityNoteChange, buttonControlled: false, variant: StyleVariant.Light, label: translate('priority_note', { namespace }) }))),
        React.createElement("div", { className: "modal__footer", style: { marginTop: '16px' } },
            AvailabilityContent,
            React.createElement("div", { style: { height: '40px', margin: 'auto' } }),
            React.createElement(Button, { type: "button", className: "mr-1", id: "charge_customer_cancel_btn", onClick: closeModal }, translate('cancel', { namespace })),
            !params.readonly && (React.createElement(Button, { variant: "primary", onClick: handleSave, disabled: params.readonly }, saving ? React.createElement(Loader, { size: LoaderSizes.small }) : translate('save', { namespace }))))));
}
export default Form;
